import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Save, RotateCcw, Clock, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const n1 = (v) => (v == null || !isFinite(v) ? "—" : v.toFixed(1).replace(".", ","));

export function EstudioHorarios({ importId }) {
  const [data, setData] = useState(null);
  const [aj, setAj] = useState({});
  const [todas, setTodas] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ocultas, setOcultas] = useState([]);

  useEffect(() => {
    setData(null);
    api.get(`/sales/imports/${importId}/horarios`).then((r) => {
      setData(r.data);
      setOcultas(r.data.ocultas || []);
      setAj(Object.fromEntries(r.data.rows.map((x) => [x.hora, { empleados: x.empleados, empleados_tipo: x.empleados_tipo }])));
    }).catch(() => setData({ rows: [] }));
  }, [importId]);

  const rows = useMemo(() => {
    if (!data) return [];
    const maxDias = Math.max(0, ...data.rows.map((r) => r.dias));
    return data.rows.filter((r) => !ocultas.includes(r.hora) && (todas || r.dias >= maxDias * 0.25)).map((r) => {
      const emp = aj[r.hora]?.empleados ?? r.empleados_calculados;
      const tipo = aj[r.hora]?.empleados_tipo;
      return { ...r, emp, tipo, ratio: emp > 0 ? r.operaciones / emp : null, ratioTipo: tipo > 0 ? r.operaciones / tipo : null };
    });
  }, [data, aj, todas, ocultas]);

  const cambiarOcultas = async (next) => {
    const prev = ocultas;
    setOcultas(next);
    try { await api.put(`/sales/imports/horarios/ocultas`, { ocultas: next }); }
    catch { setOcultas(prev); toast.error("No se pudo guardar el cambio de horas"); }
  };

  const ratios = rows.map((r) => r.ratio).filter((v) => v != null);
  const max = ratios.length ? Math.max(...ratios) : null;
  const min = ratios.length ? Math.min(...ratios) : null;
  const avisos = rows.filter((r) => r.ratioTipo != null && max != null && r.ratioTipo > max).length;
  const set = (h, k, v) => setAj((p) => ({ ...p, [h]: { ...p[h], [k]: v === "" ? null : Number(v) } }));

  const guardar = async () => {
    setSaving(true);
    try {
      await api.put(`/sales/imports/${importId}/horarios`, { ajustes: Object.fromEntries(Object.entries(aj).map(([h, v]) => [String(h), v])) });
      toast.success("Ajustes del mes guardados");
    } catch { toast.error("No se pudieron guardar los ajustes"); } finally { setSaving(false); }
  };

  if (!data) return <div className="text-slate-500 flex items-center gap-2 py-6"><Loader2 size={16} className="animate-spin" /> Calculando horarios…</div>;
  if (!data.rows.length) return <div className="card p-8 text-center text-slate-500" data-testid="horarios-empty">Esta subida no tiene la hora de cada venta.</div>;

  return (
    <div className="space-y-4" data-testid="horarios">
      <div className="card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1"><Clock size={12} /> {data.label}</div>
            <h2 className="font-display text-2xl font-bold">Estudio de horarios</h2>
            <p className="text-sm text-slate-600 mt-1 max-w-3xl">¿Hay horas en las que falta o sobra gente? Media de todos los días que la farmacia vendió en cada hora. Ajusta el N.º de empleados si hay gente en backoffice que no vende, y escribe en <b>Empleados tipo</b> los que tú pondrías.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setAj(Object.fromEntries(data.rows.map((x) => [x.hora, { empleados: null, empleados_tipo: null }])))} className="btn btn-outline btn-sm" data-testid="horarios-reset"><RotateCcw size={14} /> Valores calculados</button>
            <button onClick={guardar} disabled={saving} className="btn btn-primary btn-sm" data-testid="horarios-save">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar ajustes del mes</button>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 mt-4 text-xs">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-red-500" /> Más operaciones por empleado (falta gente): {n1(max)}</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-300" /> Menos operaciones por empleado (sobra gente): {n1(min)}</span>
          {avisos > 0 && <span className="flex items-center gap-1.5 text-red-700 font-semibold" data-testid="horarios-avisos"><AlertTriangle size={14} /> {avisos} franja(s) con tu ratio por encima del máximo</span>}
          <label className="flex items-center gap-1.5 ml-auto text-slate-500"><input type="checkbox" checked={todas} onChange={(e) => setTodas(e.target.checked)} data-testid="horarios-todas" /> Ver también horas con poca actividad</label>
        </div>
        {ocultas.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-3 text-xs" data-testid="horarios-ocultas">
            <span className="text-slate-500">Horas quitadas (en todos los meses):</span>
            {ocultas.map((h) => (
              <button key={h} onClick={() => cambiarOcultas(ocultas.filter((x) => x !== h))} title="Volver a mostrar"
                      className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-2 py-0.5 hover:bg-slate-100" data-testid={`horario-restaurar-${h}`}>
                <Undo2 size={12} /> {String(h).padStart(2, "0")}:00
              </button>
            ))}
            <button onClick={() => cambiarOcultas([])} className="text-slate-500 underline" data-testid="horarios-restaurar-todas">Mostrar todas</button>
          </div>
        )}
      </div>
      <div className="card overflow-auto">
        <table className="dense" data-testid="horarios-table">
          <thead><tr><th>Horas</th><th className="num">N.º empleados</th><th className="num">Operaciones</th><th className="num">Operaciones por empleado</th>
            <th className="num">Empleados tipo</th><th className="num">Ratio tipo</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const cls = r.ratio === max ? "bg-red-500 text-white" : r.ratio === min ? "bg-amber-300 text-slate-900" : "";
              const warn = r.ratioTipo != null && max != null && r.ratioTipo > max;
              return (
                <tr key={r.hora} data-testid={`horario-row-${r.hora}`}>
                  <td className="font-semibold whitespace-nowrap">{r.franja}<div className="text-[10px] text-slate-400 font-normal">{r.dias} días</div></td>
                  <td className="num">
                    <input type="number" min={0} step={0.5} value={aj[r.hora]?.empleados ?? ""} placeholder={n1(r.empleados_calculados)}
                           onChange={(e) => set(r.hora, "empleados", e.target.value)} className="input text-xs w-20 text-right" data-testid={`horario-emp-${r.hora}`} />
                    <div className="text-[10px] text-slate-400">calculado {n1(r.empleados_calculados)}</div>
                  </td>
                  <td className="num font-semibold">{n1(r.operaciones)}</td>
                  <td className={"num font-bold " + cls} data-testid={`horario-ratio-${r.hora}`}>{n1(r.ratio)}</td>
                  <td className="num">
                    <input type="number" min={0} step={0.5} value={aj[r.hora]?.empleados_tipo ?? ""} placeholder="—"
                           onChange={(e) => set(r.hora, "empleados_tipo", e.target.value)} className="input text-xs w-20 text-right" data-testid={`horario-tipo-${r.hora}`} />
                  </td>
                  <td className={"num font-bold " + (warn ? "text-red-700" : "")} data-testid={`horario-ratio-tipo-${r.hora}`}>
                    <span className="inline-flex items-center gap-1">{warn && <AlertTriangle size={14} className="text-red-600" data-testid={`horario-warning-${r.hora}`} />}{n1(r.ratioTipo)}</span>
                    {warn && <div className="text-[10px] font-semibold text-red-600">por encima del máximo ({n1(max)})</div>}
                  </td>
                  <td>
                    <button onClick={() => cambiarOcultas([...ocultas, r.hora].sort((a, b) => a - b))} title="Quitar esta hora del estudio"
                            className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`horario-quitar-${r.hora}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500">Operaciones = ventas medias en esa hora. N.º empleados = media de personas distintas que vendieron en esa hora (se puede ajustar). Ratio tipo = operaciones / empleados tipo.</p>
    </div>
  );
}
